import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { assetsReportsAPI, tenantUsersAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/Table';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import { LOCATION_TYPES, labelFor, formatAssetCurrency } from '../../../lib/assetConstants';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export default function InsuranceLocationReport() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    reportType: 'active',
    locationType: '',
    assignedUserId: '',
    status: '',
  });
  const [activeFilters, setActiveFilters] = useState(filters);
  const [exporting, setExporting] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    tenantUsersAPI.list().then((res) => {
      const list = res?.users || res || [];
      setUsers(Array.isArray(list) ? list : []);
    }).catch(console.error);
  }, []);

  const { data, isLoading } = useQuery(
    ['assets-reports', 'insurance', activeFilters],
    () => assetsReportsAPI.getInsurance(activeFilters)
  );

  const handleApply = () => setActiveFilters({ ...filters });

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    try {
      setExporting('csv');
      const blob = await assetsReportsAPI.exportInsuranceCsv(activeFilters);
      const dateStr = new Date().toISOString().split('T')[0];
      downloadBlob(blob, `assets-insurance-${dateStr}.csv`);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExporting('pdf');
      const blob = await assetsReportsAPI.exportInsurancePdf(activeFilters);
      const dateStr = new Date().toISOString().split('T')[0];
      downloadBlob(blob, `assets-insurance-${dateStr}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/tenant/assets/reports')} className="mb-4 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Reports
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Insurance & location report</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Export active assets, by location, or lost/stolen items for insurance records.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportCsv} disabled={!!exporting}>
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={handleExportPdf} disabled={!!exporting}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        <Card className="mb-6 bg-white dark:bg-gray-800">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <FormLabel>Report type</FormLabel>
                <select
                  value={filters.reportType}
                  onChange={(e) => setFilters((f) => ({ ...f, reportType: e.target.value }))}
                  className={selectClass}
                >
                  <option value="active">Active assets</option>
                  <option value="insurance">Insurance valuation</option>
                  <option value="lost_stolen">Lost / stolen</option>
                </select>
              </div>
              <div>
                <FormLabel>Location type</FormLabel>
                <select
                  value={filters.locationType}
                  onChange={(e) => setFilters((f) => ({ ...f, locationType: e.target.value }))}
                  className={selectClass}
                >
                  <option value="">All</option>
                  {LOCATION_TYPES.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <FormLabel>Assigned to</FormLabel>
                <select
                  value={filters.assignedUserId}
                  onChange={(e) => setFilters((f) => ({ ...f, assignedUserId: e.target.value }))}
                  className={selectClass}
                >
                  <option value="">Anyone</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={handleApply} className="w-full">Apply filters</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {data && (
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {data.count} asset(s) · Total replacement value:{' '}
            <strong className="text-gray-900 dark:text-gray-100">
              {formatAssetCurrency(data.totalReplacementValue)}
            </strong>
          </p>
        )}

        <Card className="bg-white dark:bg-gray-800 overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Results</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead className="text-right">Replacement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.assets || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No assets match these filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.assets.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Link to={`/tenant/assets/${a.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                            {a.assetRef}
                          </Link>
                        </TableCell>
                        <TableCell>{a.name}</TableCell>
                        <TableCell>{a.status}</TableCell>
                        <TableCell>
                          {[labelFor(LOCATION_TYPES, a.locationType), a.locationNotes].filter(Boolean).join(' · ') || '—'}
                        </TableCell>
                        <TableCell>{a.assignedUserName || a.assignedVehicleReg || '—'}</TableCell>
                        <TableCell className="text-right">
                          {formatAssetCurrency(a.replacementValue ?? a.currentValue)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
